import React, { useCallback, useMemo, useState } from "react";
import { Col, Divider, Form, Modal, Row, Select, Space, Spin } from "antd";
import { makeStyles } from "@material-ui/core/styles";
import CustomButton from "components/Common/buttons/CustomButton";
import NGIForm from "components/Common/form/NGIForm";
import Title from "antd/lib/typography/Title";
import classnames from "classnames";
import NGIFormItem from "components/Common/form/NGIFormItem";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { debounce } from "lodash";
import NGIInput from "components/Common/inputs/NGIInput";
import { VALIDATION_RULES } from "pages/price-management/CustomerBlacklist/variables";
import { addCustomerToBlacklist, fetchCustomerById, retrieveCustomerPhotoUrlByType } from "utils/api";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import userIcon from "assets/images/svg/user-not-found.svg";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import NGISelect from "components/Common/inputs/NGISelect";
import CustomLink from "components/Common/buttons/CustomLink";
const { Option } = Select;

const useStyles = makeStyles({
  modal: {
    maxWidth: "900px!important",
  },
  infoContainer: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span:first-of-type": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  formItem: {
    display: "flex",

    "& .ant-col-12 + .ant-col-12": {
      paddingLeft: 10,
    },
  },
  disabled: {
    pointerEvents: "none",
    opacity: 0.6,
  },
  fileContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 15,
    border: "1px solid rgba(0, 0, 0, 0.05)",
    padding: "5px 10px",
    marginTop: 10,
  },
  removeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
  fileTitle: {
    fontWeight: 600,
    fontSize: 14,
  },
  customerPreview: {
    display: "flex",
    padding: 10,
    border: "1px solid #FF00004D",
    borderRadius: 5,
    alignItems: "center",
  },
  imgContainer: {
    width: 36,
    height: 36,
    marginRight: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#6666661A",
    borderRadius: 3,
    overflow: "hidden",
  },
  imgFound: {
    width: 36,
  },
  found: {
    borderColor: "#0000000F",
  },
  customerName: {
    fontWeight: 600,
    lineHeight: "15px",
  },
  customerRole: {
    color: "#8C8C8C",
    fontWeight: 300,
    fontSize: 11,
  },
});

const AddCustomerModal = ({ onClose, update }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isSearchCustomer, setIsSearchCustomer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [foundCustomerImg, setFoundCustomerImg] = useState(null);

  const {
    list: { blacklistAddingReason },
  } = useSelector(DictionariesSelector);

  const onSave = useCallback(values => {
    setIsLoading(true);
    addCustomerToBlacklist(values)
      .then(() => {
        successMessage("Customer was added to blacklist successfully");
        setIsLoading(false);
        setTimeout(() => update && update(), 1500);
        onClose();
      })
      .catch(e => {
        setIsLoading(false);
        defaultNgiErrorMethod(e);
      });
  }, []);

  const changeHandler = useCallback(
    event => {
      setFoundCustomer(null);
      setFoundCustomerImg(null);
      const id = event.target.value;
      if (id.includes(" ")) return;
      if (id) {
        setIsSearchCustomer(true);
        fetchCustomerById(id)
          .then(async ({ data }) => {
            setIsSearchCustomer(false);
            setFoundCustomer(data.result);
            retrieveCustomerPhotoUrlByType(id, "personalPhoto")
              .then(
                ({
                  data: {
                    result: { url },
                  },
                }) => {
                  setFoundCustomerImg(url);
                }
              )
              .catch(() => {
                setFoundCustomerImg(null);
              });
          })
          .catch(() => {
            setIsSearchCustomer(false);
            setFoundCustomer(null);
            setFoundCustomerImg(null);
            warnMessage("Customer is not found");
          });
      }
    },
    [form]
  );

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const debouncedChangeHandler = useMemo(() => debounce(changeHandler, 1000), []);

  return (
    <Modal
      title={
        <div className={classes.infoContainer}>
          Blacklist Customer
          <span>Please fill in all mandatory fields</span>
        </div>
      }
      className={classes.modal}
      open
      onCancel={onClose}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <NGIForm name="addCustomer" form={form} onFinish={onSave}>
          <Divider />
          <Row className={classes.formItem}>
            <Col span={12}>
              <Title level={5}>Customer ID*</Title>
              <NGIFormItem name="customerId" onChange={debouncedChangeHandler} rules={VALIDATION_RULES}>
                <NGIInput placeholder="Customer ID" suffix={<Spin size="small" spinning={isSearchCustomer} />} />
              </NGIFormItem>
            </Col>
            <Col span={12}>
              <Title level={5}>Reason for Adding*</Title>
              <NGIFormItem name="addReason" rules={VALIDATION_RULES}>
                <NGISelect showSearch placeholder="Select value">
                  {blacklistAddingReason?.map(item => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </NGISelect>
              </NGIFormItem>
            </Col>
          </Row>
          <Row className={classes.formItem}>
            <Col span={24}>
              <Title level={5}>Additional Comments</Title>
              <NGIFormItem name="comments">
                <NGIInput placeholder="Additional Comments" />
              </NGIFormItem>
            </Col>
          </Row>
          <Divider />
          <Title level={5}>Preview Result</Title>
          <div className={classnames(classes.customerPreview, !!foundCustomer && classes.found)}>
            <div className={classes.imgContainer}>
              <img
                className={classes.imgFound}
                src={foundCustomerImg ? foundCustomerImg : userIcon}
                alt="customerIcon"
              />
            </div>
            <div className={classes.customerData}>
              <div className={classes.customerName}>
                {foundCustomer ? (
                  <CustomLink
                    color={"#0085FF"}
                    underlined
                    target="_blank"
                    to={`/customers/customer/${foundCustomer.id}`}
                  >
                    {foundCustomer.firstName} {foundCustomer.lastName}
                  </CustomLink>
                ) : (
                  "No User Found"
                )}
              </div>
              <div className={classes.customerRole}>{foundCustomer ? foundCustomer.occupation : "-----"}</div>
            </div>
          </div>
        </NGIForm>
      </Spin>
      <Divider />
      <br />
      <Space>
        <CustomButton onClick={onClose} type="primary" size="small" color="outlined" text="Cancel" />
        <CustomButton
          disabled={!foundCustomer}
          type="primary"
          onClick={openConfirmationModal}
          text="CONFIRM"
          size="small"
        />
      </Space>

      {isConfirmationOpen && (
        <ConfirmationModal
          description="Are you sure you want to add customer to the blacklist?"
          onCancel={closeConfirmationModal}
          onConfirm={form.submit}
          loading={isLoading}
        />
      )}
    </Modal>
  );
};

export default AddCustomerModal;
